import {makeAutoObservable, observable, runInAction} from 'mobx'
import {Lang} from 'yougile-lang'
import {logger, Time} from 'yougile-util'
import {
  createDataObjectId,
  crmDealAmountStickerId,
  DataField,
  DataObjectId,
  Hub,
  StickerType,
  Tag,
  youGileUserId
} from 'yougile-model'
import {Sync} from '@client/sync'
import {createTask} from '@components/add-task/create-task'
import {globals} from '@extensions/globals-base'
import {PageService} from '@extensions/page-service'
import {Sticker} from '@extensions/sticker/sticker'
import {SubtaskUtil} from '@extensions/subtask-util'
import {getProjectByTask, Task} from '@extensions/task'

interface State {
  isCrmEnabled: boolean
  newDealColumnId: DataObjectId
}

const calcCrmProject = (): boolean => {
  if (!crmStore.isCrmEnabled) {
    return false
  }
  const projectHub = PageService.getCurrentProject()
  return projectHub?.data?.projectType === 'crm'
}

const disallowedSystemStickersInCrm: StickerType[] = ['repeat'] as const

export class CrmStore {
  state: State = {
    isCrmEnabled: false,
    newDealColumnId: undefined
  }

  private removeListeners: () => void
  private cacheIsCurrentProjectCrm = false

  constructor() {
    makeAutoObservable(this, {state: observable.shallow}, {autoBind: true})
  }

  get isCrmEnabled(): boolean {
    return this.state.isCrmEnabled
  }

  get isCurrentProjectCrm(): boolean {
    return this.cacheIsCurrentProjectCrm
  }

  get isNewDealPopupOpened(): boolean {
    return Boolean(this.state.newDealColumnId)
  }

  public updateCrmEnabled(isCrmEnabled: boolean | undefined): void {
    if (isCrmEnabled === undefined) {
      return
    }

    this.state.isCrmEnabled = isCrmEnabled
    this.cacheIsCurrentProjectCrm = calcCrmProject()
  }

  public addCrmProjectListeners(): void {
    const listenerId = PageService.addLocationListener(() => {
      runInAction(() => {
        this.cacheIsCurrentProjectCrm = calcCrmProject()
      })

      Lang.setContextPredicate(() => {
        if (crmStore.isCurrentProjectCrm) {
          return 'crm'
        }
        return undefined
      })
    }, false)

    const onCompanyChangeListener = globals.onCompanyChange(() => {
      const extensions = globals.initEnabledExtensions?.[globals.getCompanyId()]
      if (extensions?.length) {
        this.updateCrmEnabled(extensions.includes('crm'))
      }
    })

    this.removeListeners = () => {
      PageService.removeLocationListener(listenerId)
      globals.removeCompanyListener(onCompanyChangeListener)
    }
  }

  public createNewDeal(data: {
    title: string
    contactPerson: Hub | string
    organization: Hub | string
    dealAmount: string
    fields?: DataField[]
  }): void {
    const normalizedTitle = data.title.trim()
    if (!normalizedTitle.length) {
      return
    }

    const dealId = createTask({
      text: normalizedTitle,
      stickers: [],
      columnId: crmStore.state.newDealColumnId
    })

    if (!dealId) {
      throw new Error('Failed to create task')
    }

    const deal = Task.getById(dealId)
    if (!deal) {
      throw new Error('Failed to get deal')
    }

    deal.stickers[crmDealAmountStickerId] = data.dealAmount.trim()
    const id = createDataObjectId()
    deal.data.dealActivity = {
      [id]: {
        id,
        from: youGileUserId,
        type: 'created',
        timestamp: Time.now()
      }
    }
    deal.commit()

    this.linkContactPerson(deal, data.contactPerson)
    this.linkOrganization(deal, data.organization)

    this.closeNewDealPopup()
  }

  public showNewDealPopup(columnId: DataObjectId): void {
    this.state.newDealColumnId = columnId
  }

  public closeNewDealPopup(): void {
    this.state.newDealColumnId = undefined
  }

  public dispose(): void {
    this.removeListeners?.()
  }

  public isCrmLocation(location: DataObjectId): boolean {
    const box = Sync.getCurrentSandbox()
    const hub = box.getHub(location)

    if (!hub) {
      return false
    }

    if (this.isCrmBoard(hub) || this.isCrmProject(hub)) {
      return true
    }

    const parentHub = box.getHub(hub.location)
    if (parentHub) {
      return this.isCrmLocation(parentHub.location)
    }

    return false
  }

  public isCrmProject(project: Hub): boolean {
    return this.isCrmEnabled && project?.data.projectType === 'crm'
  }

  public isCrmBoard(board: Hub): boolean {
    if (!board) {
      return false
    }

    const box = Sync.getCurrentSandbox()
    const project = box.getHub(board?.location)
    return this.isCrmEnabled && project?.data.projectType === 'crm'
  }

  public isCrmColumn(column: Hub): boolean {
    return this.isCrmEnabled && this.isCrmLocation(column.location)
  }

  public isStickerAllowedOnBoard(board: Hub, sticker: Tag): boolean {
    return (
      this.isCrmBoard(board) &&
      !disallowedSystemStickersInCrm.includes(Sticker.getType(sticker))
    )
  }

  public isDeal(hub?: Hub): boolean {
    if (!hub) {
      return false
    }

    const {project} = getProjectByTask(hub.id) ?? {}
    return project && this.isCrmProject(project)
  }

  isSucceededColumn(hub?: Hub): boolean {
    return (
      this.isCrmEnabled &&
      (hub.data.crmSettings?.isSucceededColumn ??
        // NOTE: Maintains backward compatibility with the beta version
        // of the CRM, where "succeeded" columns were identified solely
        // by their title, without any specific flags.
        hub.title === Lang.msg.crm.board.succeededColumnTitle)
    )
  }

  private addDealToSubtasks(parentHub: Hub, deal: Hub): void {
    const subTask = parentHub.data.subtaskList ?? {
      order: [],
      subtasks: {}
    }
    const subTaskList = SubtaskUtil.addSubtasks(subTask, [deal])
    parentHub.data.subtaskList = subTaskList
    parentHub.commit()
  }

  isRejectedColumn(hub?: Hub): boolean {
    return (
      this.isCrmEnabled &&
      (hub.data.crmSettings?.isRejectedColumn ??
        // NOTE: Maintains backward compatibility with the beta version
        // of the CRM, where "rejected" columns were identified solely
        // by their title, without any specific flags.
        hub.title === Lang.msg.crm.board.rejectedColumnTitle)
    )
  }

  public getOrganization(dealId: DataObjectId): Hub | undefined {
    const {project} = getProjectByTask(dealId) ?? {}
    const orgLoc = project?.data?.projectSettings?.organizationsLocation
    const parentIds = SubtaskUtil.getParentTaskIds(dealId)
    return parentIds.map(Task.getById).find(t => t?.location === orgLoc)
  }

  public getContactPerson(dealId: DataObjectId): Hub | undefined {
    const {project} = getProjectByTask(dealId) ?? {}
    const contactLoc = project?.data?.projectSettings?.contactPersonsLocation
    const parentIds = SubtaskUtil.getParentTaskIds(dealId)
    return parentIds.map(Task.getById).find(t => t?.location === contactLoc)
  }

  public unlinkOrganization(dealId: DataObjectId): void {
    const parent = this.getOrganization(dealId)
    const subtasks = SubtaskUtil.getSubtaskList(parent)
    parent.data.subtaskList = SubtaskUtil.deleteSubtask(subtasks, [dealId])
    parent.commit()
  }

  public unlinkContactPerson(dealId: DataObjectId): void {
    const parent = this.getContactPerson(dealId)
    const subtasks = SubtaskUtil.getSubtaskList(parent)
    parent.data.subtaskList = SubtaskUtil.deleteSubtask(subtasks, [dealId])
    parent.commit()
  }

  public linkOrganization(deal: Hub, organization: Hub | string): void {
    if (this.getOrganization(deal.id)) {
      logger.log(`Deal ${deal.id} already have linked organization. Skip`)
      return
    }

    const {project} = getProjectByTask(deal.id) ?? {}
    let organizationHub: Hub

    if (!project) {
      throw new Error('Project not found')
    }

    if (typeof organization === 'string') {
      const normalizedTitle = organization.trim()
      if (normalizedTitle.length === 0) {
        logger.warn('Organization title should not be empty. Skipped')
        return
      }

      const newContactTaskId = createTask({
        text: normalizedTitle,
        stickers: [],
        columnId: project.data.projectSettings.organizationsLocation
      })
      organizationHub = Task.getById(newContactTaskId)
      if (!organizationHub) {
        throw new Error('Failed to get just created organization hub')
      }
    } else {
      organizationHub = organization
    }

    if (organizationHub) {
      this.addDealToSubtasks(organizationHub, deal)
    }
  }

  public linkContactPerson(deal: Hub, contactPerson?: Hub | string): void {
    if (this.getContactPerson(deal.id)) {
      logger.log(`Deal ${deal.id} already have linked contact person. Skip`)
      return
    }

    const {project} = getProjectByTask(deal.id) ?? {}
    let contactPersonHub: Hub

    if (!project) {
      throw new Error('Project not found')
    }

    if (typeof contactPerson === 'string') {
      const normalizedTitle = contactPerson.trim()
      if (normalizedTitle.length === 0) {
        logger.warn('Organization title should not be empty. Skipped')
        return
      }

      const newContactTaskId = createTask({
        text: normalizedTitle,
        stickers: [],
        columnId: project.data.projectSettings?.contactPersonsLocation
      })
      if (!newContactTaskId) {
        throw new Error('Failed to create contact person task')
      }

      contactPersonHub = Task.getById(newContactTaskId)
      if (!contactPersonHub) {
        throw new Error('Failed to get just created contact person hub')
      }
    } else {
      contactPersonHub = contactPerson
    }

    if (contactPersonHub) {
      this.addDealToSubtasks(contactPersonHub, deal)
    }
  }

  public getProjectOrganizations(project: Hub): Hub[] {
    const orgLoc = project?.data?.projectSettings?.organizationsLocation
    if (!orgLoc) {
      logger.warn(
        `CRM organizations location in project ${project?.id} not found. Skip`
      )
      return []
    }
    const box = Sync.get(project)
    return box?.getChildrenHubs(orgLoc) ?? []
  }

  public getProjectContactPersons(project: Hub): Hub[] {
    const contactLoc = project?.data?.projectSettings?.contactPersonsLocation
    if (!contactLoc) {
      logger.warn(
        `CRM contact persons location in project ${project?.id} not found. Skip`
      )
      return []
    }
    const box = Sync.get(project)
    return box?.getChildrenHubs(contactLoc) ?? []
  }
}

export const crmStore = new CrmStore()
